import { Megaphone, AlertTriangle, FileText, Activity, Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import type { PatientAnnouncementSummary } from "@/hooks/use-patient-announcements";

const CATEGORY_META = {
  billing: {
    label: "Billing",
    className: "bg-warning/15 text-warning border-warning/40",
    icon: FileText,
  },
  alert: {
    label: "Alert",
    className: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-800",
    icon: AlertTriangle,
  },
  clinical: {
    label: "Clinical",
    className: "bg-chart-4/10 text-chart-4 border-indigo-200 dark:border-indigo-800",
    icon: Activity,
  },
  admin: {
    label: "Admin",
    className: "bg-muted text-foreground/80 border-border",
    icon: Lock,
  },
} as const;

function summaryLabel(summary: PatientAnnouncementSummary): string {
  const title = summary.primary_title?.trim();
  if (title) return title.length > 42 ? `${title.slice(0, 42)}…` : title;

  switch (summary.primary_category) {
    case "billing":
      return "Billing notice";
    case "alert":
      return "Safety alert";
    case "clinical":
      return "Clinical flag";
    default:
      return "Staff notice";
  }
}

interface PatientAnnouncementIndicatorProps {
  summary?: PatientAnnouncementSummary;
  size?: "sm" | "md";
  className?: string;
}

export function PatientAnnouncementIndicator({
  summary,
  size = "sm",
  className,
}: PatientAnnouncementIndicatorProps) {
  if (!summary || summary.count <= 0) return null;

  const meta = CATEGORY_META[summary.primary_category] ?? CATEGORY_META.admin;
  const Icon = meta.icon;
  const label = summaryLabel(summary);

  return (
    <span
      title={summary.primary_title}
      className={cn(
        "inline-flex max-w-full items-center gap-1 rounded-lg border font-black uppercase tracking-widest shrink-0",
        meta.className,
        size === "sm" ? "px-1.5 py-0.5 text-[8px]" : "px-2 py-1 text-[9px]",
        className,
      )}
    >
      <Megaphone className={cn(size === "sm" ? "h-2.5 w-2.5" : "h-3 w-3", "shrink-0")} />
      <Icon className={cn(size === "sm" ? "h-2.5 w-2.5" : "h-3 w-3", "shrink-0 opacity-80")} />
      <span className="truncate">{label}</span>
      {summary.count > 1 && (
        <span className="rounded bg-black/10 px-1 py-px text-[7px]">{summary.count}</span>
      )}
    </span>
  );
}

/** Row helper: patient search result with optional announcement badge on the right. */
export function PatientSearchRowExtras({
  patientId,
  summaries,
}: {
  patientId: string;
  summaries?: Record<string, PatientAnnouncementSummary>;
}) {
  const summary = summaries?.[patientId];
  if (!summary) return null;

  return (
    <div className="ml-auto shrink-0 max-w-[45%]">
      <PatientAnnouncementIndicator summary={summary} />
    </div>
  );
}
